/**
 * The interstitial and the results screen: the one screen no game supplies, drawn
 * entirely from `core.show`. The unhosted countdown is a `timers` row arriving as
 * `core.deadlineAt`; its absence means "held".
 */

import type { JsonObject, Snapshot } from '@gsf/contract';
import { useCountdown } from '../lib/useSession';

export type ShowSurface = 'host' | 'display' | 'player';

export interface InterstitialProps {
  readonly snapshot: Snapshot;
  /** `GameUiProps.send`; a `core:*` action rides the same envelope. */
  readonly send: (type: string, payload?: JsonObject) => void;
  readonly surface: ShowSurface;
  readonly connected: boolean;
}

export function Interstitial({ snapshot, send, surface, connected }: InterstitialProps) {
  const { core } = snapshot;
  const { show } = core;
  const finished = core.stage === 'results';
  // A driving display holds the host's authority, so it gets the controls.
  const canDrive = surface === 'host' || (surface === 'display' && core.displayCanDrive);

  const unhosted = core.hostMode === 'unhosted';
  // At an unhosted interstitial the only armed timer is the auto-advance, so
  // `core.deadlineAt` is it. Null means held.
  const seconds = useCountdown(unhosted && !finished ? core.deadlineAt : null, core.serverNow);
  const leader = snapshot.you.isPartyLeader;

  const played = show.segments.filter((segment) => segment.status === 'complete');

  return (
    <div className="show-screen">
      <header className="show-head">
        <h1>{finished ? 'Final results' : 'Standings'}</h1>
        <p className="muted">
          {finished
            ? `${core.title} — ${String(played.length)} round${played.length === 1 ? '' : 's'} played`
            : show.next
              ? `Up next: ${show.next.title}`
              : 'The show is between rounds.'}
        </p>
        {/* In the header so it reads on a TV from a sofa. Exactly one of this and the
            "Held" line below is on screen. */}
        {unhosted && !finished && seconds !== null ? (
          <p className="show-countdown">
            starting in <strong>{seconds}s</strong>
          </p>
        ) : null}
      </header>

      {show.summary ? (
        <section className="show-summary card col">
          <h2>{show.summary.headline}</h2>
          {show.summary.lines.map((line, at) => (
            <p key={at} className="muted">
              {line}
            </p>
          ))}
        </section>
      ) : null}

      {show.standings.length > 0 ? (
        <ol className="show-standings">
          {show.standings.map((standing) => (
            <li key={standing.target.id}>
              {/* The rank, not the row position: tied players share it. */}
              <span className="show-rank">{standing.rank}</span>
              <span className="show-name">{standing.displayName}</span>
              {/* What this round paid, next to the total it moved, so scoring isn't opaque.
                  Absent for a player not in the segment that just closed. */}
              {standing.lastSegment ? (
                <span className="show-delta">
                  {standing.lastSegment.awarded >= 0 ? '+' : ''}
                  {standing.lastSegment.awarded}
                </span>
              ) : (
                <span className="show-delta faint">—</span>
              )}
              <span className="lb-score">{standing.showTotal}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">Nothing has been scored yet.</p>
      )}

      <RunningOrder snapshot={snapshot} />

      {canDrive && !finished ? (
        <div className="show-controls">
          <button
            className="btn btn-primary btn-lg"
            disabled={!connected || !show.next}
            onClick={() => send('core:advanceSegment')}
          >
            {show.next ? `Start ${show.next.title}` : 'Nothing to start'}
          </button>
          <button
            className="btn"
            disabled={!connected || !show.next}
            onClick={() => send('core:skipSegment')}
          >
            Skip {show.next ? show.next.title : 'it'}
          </button>
          <button className="btn" disabled={!connected} onClick={() => send('core:endShow')}>
            End the show
          </button>
        </div>
      ) : null}

      {/* Held, or the party leader's buttons. A TV counting down draws nothing here, so
          no empty controls strip. */}
      {unhosted && !finished && !canDrive && (seconds === null || leader) ? (
        <div className="show-controls col">
          {seconds === null ? (
            <p className="faint show-waiting">
              Held.{' '}
              {leader
                ? 'Start the next round when the room is ready.'
                : 'The party leader will start the next round.'}
            </p>
          ) : null}

          {/* Only the party leader may send these; never draw a control the server refuses. */}
          {leader ? (
            <div className="row" style={{ gap: '0.5rem' }}>
              <button
                className="btn btn-primary"
                disabled={!connected || !show.next}
                onClick={() => send('core:advanceSegment')}
              >
                {show.next ? `Start ${show.next.title}` : 'Nothing to start'}
              </button>
              {seconds !== null ? (
                <button
                  className="btn"
                  disabled={!connected}
                  onClick={() => send('core:holdShow')}
                  title="Stop the countdown — the round starts when you say so"
                >
                  Hold
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {!unhosted && !canDrive && !finished ? (
        <p className="faint show-waiting">Waiting for the host to start the next round.</p>
      ) : null}
    </div>
  );
}

/**
 * The running order. The hint stays empty when it would repeat the title: an unnamed
 * segment's title falls back to the game's name.
 */
function RunningOrder({ snapshot }: { snapshot: Snapshot }) {
  const { segments, next } = snapshot.core.show;
  const live = snapshot.core.segment?.index;
  return (
    <ol className="show-order">
      {segments.map((segment) => {
        const hint =
          segment.status === 'skipped'
            ? 'skipped'
            : segment.status === 'complete'
              ? 'played'
              : segment.index === live
                ? 'now'
                : segment.index === next?.index
                  ? 'up next'
                  : segment.gameName;
        return (
          <li key={segment.index} className={`is-${segment.status}`}>
            <span className="show-order-name">{segment.title}</span>
            <span className="faint">{hint === segment.title ? '' : hint}</span>
          </li>
        );
      })}
    </ol>
  );
}
